<template>
  <div class="add-to-basket">
    <input v-if="!hideQuantity"
           v-model.number="quantity"
           type="number"
           :min="minQuantity"
           :max="maxQuantity"
           class="form-control add-to-basket__quantity"
           :class="{'form-control--invalid': !valid}"
           :disabled="quantityDisabled"
           placeholder="Quantity"
           @change="onQuantityChange"
           @keyup.enter="onEnterPressed">

    <processing-button :variant="variant"
                       :disabled="disabled"
                       :action="addToBasket"
                       :trigger-action="enterPressed">
      <template #default>
        <span>{{ label }}</span>
      </template>
    </processing-button>
  </div>
</template>

<script>
import { BasketResource } from "spiritweb.packages.ecommerce";
import ProcessingButton from "~/Components/Base/ProcessingButton.vue";

export default {
  name: "AddToBasket",
  components: {
    ProcessingButton,
  },
  props: {
    label: {
      type: String,
      default: "Add to Basket",
    },
    productId: {
      type: Number,
      required: true,
    },
    pricingScheduleId: {
      type: Number,
      required: true,
    },
    productFormatId: {
      type: Number,
      required: true,
    },
    productLicenceTypeId: {
      type: Number,
      required: true,
    },
    defaultQuantity: {
      type: Number,
      default: null,
    },
    quantityDisabled: {
      type: Boolean,
      default: false,
    },
    minQuantity: {
      type: Number,
      default: 1,
    },
    maxQuantity: {
      type: Number,
      default: null,
    },
    hideQuantity: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: "btn btn-primary btn--shrink",
    },
  },
  data () {
    return {
      loading: false,
      quantity: null,
      valid: true,
      enterPressed: false,
    };
  },
  watch: {
    quantity: {
      immediate: true,
      handler ( val ) {
        this.$emit( "selected-quantity", val );
      },
    },
    defaultQuantity: {
      immediate: true,
      handler ( val ) {
        this.quantity = val;
      },
    },
  },
  methods: {
    onEnterPressed () {
      this.enterPressed = true;
    },
    onQuantityChange () {
      this.quantity = Math.max( this.minQuantity,
        Math.min( this.maxQuantity ?? Number.POSITIVE_INFINITY, this.quantity ) );
    },
    checkValid () {
      const valid = this.quantity !== null && this.quantity !== undefined && this.quantity !== "";
      if ( valid ) {
        this.valid = Number.isInteger( this.quantity );
      } else {
        this.valid = false;
      }
    },
    addToBasket () {
      this.checkValid();

      if ( !this.valid ) {
        this.enterPressed = false;
        return Promise.reject( new Error( "Quantity is not valid" ) );
      }

      const options = {
        pricingScheduleId: this.pricingScheduleId,
        productFormatId: this.productFormatId,
        productLicenceTypeId: this.productLicenceTypeId,
      };

      return BasketResource.addProduct(
        this.productId, this.quantity, options,
      ).then( ( response ) => {
        if ( response?.data?.length ) {
          const [result] = response.data;

          if ( result?.orderLineResult?.errorCode !== 100 ) {
            this.$emit( "fail", result?.orderLineResult?.errorMessage, result?.orderLineResult?.errorCode );
            return Promise.reject( new Error( `Unsuccessful error code: ${result?.orderLineResult?.errorCode}` ) );
          }

          // Signal to any baskets that they should update
          this.$root.$emit( "sw::setBasketItemQuantity::response" );
          this.$bvToast.show( "product-added" );
          return Promise.resolve();
        }
        return Promise.reject( new Error( "Response did not have data" ) );
      } ).finally( () => {
        this.enterPressed = false;
      } );
    },
  },
};
</script>

<style lang="less" scoped>
.add-to-basket {

  .btn {
    font-size: 13px;

    /deep/ svg {
      margin: 0;
    }
  }

  &__quantity {
    max-width: 85px;
    -moz-appearance: textfield;

    &::placeholder {
      font-size: 13px;
      text-align: center;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}
</style>
